import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { OpCode, Select } from '@aline/common';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { AlineInput } from 'src/AlineInput';
import type { PanelVariableType } from 'src/components';
import {
  AlineTooltip,
  useOnChangeFocusVariable,
  useUpdatePrimitiveVariableValue,
  useVariableStyles,
  VariableRowHeader,
} from 'src/components';
import { IinBoxIcon } from 'src/components/icons/IinBoxIcon';
import { useGetFieldDisabledReason } from 'src/editor/popovers';
import { domElIdCreators } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  endAdornment: {
    marginRight: theme.spacing(1),
  },
  multiline: {
    padding: 0,
  },
}));

interface MetadataTextValueProps extends React.HTMLAttributes<HTMLElement> {
  documentId: UUID;
  isSelected: boolean;
  textVariable: PanelVariableType;
}

export function TextVariable({
  className,
  documentId,
  isSelected,
  textVariable,
}: MetadataTextValueProps): JSX.Element {
  const { classes: textVarClasses } = useStyles();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const { disabledReason } = useGetFieldDisabledReason(textVariable);
  const [liEl, setLiEl] = useState<HTMLLIElement | null>(null);
  const [value, setValue] = useState<string>(textVariable.value ?? '');
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const { onClickVariable, resetOnBlur, updatePrimitiveVariable } = useUpdatePrimitiveVariableValue(
    textVariable,
    {
      isFocused,
      syncVariableValue: v => setValue(v.value ?? ''),
    }
  );
  const isCompleted =
    typeof textVariable.value === 'string' || Boolean(textVariable.selectedAnswer);
  const { classes } = useVariableStyles({ isCompleted, isSelected });

  const { onFocus } = useOnChangeFocusVariable(textVariable, { inputEl, liEl });

  return (
    <li
      ref={setLiEl}
      aria-selected={isSelected}
      className={clsx(className, classes.variable)}
      data-cy={Select.document.editor.rightDrawer.variable.list.row.container}
      id={domElIdCreators.variableRowEl(textVariable.nodeId)}
      onClick={onClickVariable}
      onFocus={onFocus}
    >
      <VariableRowHeader
        documentId={documentId}
        isCompleted={isCompleted}
        variable={textVariable}
      />
      {textVariable.question && (
        <Typography className={classes.question}>{textVariable.question}</Typography>
      )}
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AlineInput
            multiline
            classes={{ root: textVarClasses.multiline }}
            dataCy={Select.document.editor.rightDrawer.outline.textFieldRowInput}
            disabled={Boolean(disabledReason)}
            endAdornment={<IinBoxIcon className={textVarClasses.endAdornment} />}
            inputClassName={classes.input}
            inputRef={setInputEl}
            placeholder="Enter text..."
            value={value}
            onBlur={() => {
              const opCode = resetOnBlur();
              if (opCode === OpCode.Success) setValue('');
              setIsFocused(false);
            }}
            onChange={async e => {
              setValue(e.target.value);
              await updatePrimitiveVariable({
                formattedValue: e.target.value,
                value: e.target.value,
              });
            }}
            onFocus={e => {
              e.target.select();
              setIsFocused(true);
            }}
          />
        </div>
      </AlineTooltip>
    </li>
  );
}
